import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/* API helper */
import { api } from "../api";

/* Context */
import { useAuthContext } from "../context/AuthContext";

/* Utils */
import { validatePassword } from '../utils/passwordUtils';

/* CSS */
import AuthHeader from '../components/layout/AuthHeader';
import shared from '../styles/shared.module.css';
import styles from "./ResetPasswordPage.module.css";

/* Components */
import PasswordInput from '../components/auth/PasswordInput';
import PasswordChecklist from '../components/auth/PasswordChecklist';

/* Icons */
import exclamation from '../assets/icons/exclamation_red.svg';

const ResetPasswordPage = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const { login } = useAuthContext();

    // Carried over from the code-verification step
    const email = location.state?.email || "";
    const code = location.state?.code || "";

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showErrors, setShowErrors] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // Same validation rules as signup
    const criteria = validatePassword(password, confirmPassword);
    const isFormValid = Object.values(criteria).every(Boolean);

    const handleReset = async () => {
        if (!isFormValid) {
            setShowErrors(true);
            return;
        }
        try {
            setIsLoading(true);
            setError("");
            // The code is re-checked server-side here — the real security gate
            const data = await api.post('/auth/reset-password', { email, code, newPassword: password });
            // Auto-login with the fresh token, just like finishing signup
            login(data.token, data.user);
            navigate('/');
        } catch (err) {
            console.error("Error resetting password: ", err);
            setError(err.message || "Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <AuthHeader showCancel={false} />

            <div className={shared.authBody}>
                <div className={shared.authTitleWrapper}>
                    <div className={shared.authTitle} style={{ fontWeight: '900' }}>Create your new password</div>
                </div>

                <div className={shared.authBodyInput}>

                    {/* Password field */}
                    <PasswordInput
                        label="Your new password"
                        value={password}
                        onChange={setPassword}
                        labelStyle={{ fontWeight: '600' }}
                    />

                    {/* Password criteria list */}
                    <PasswordChecklist criteria={criteria} showErrors={showErrors} />

                    {/* Confirm password */}
                    <PasswordInput
                        label="Confirm your new password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        labelStyle={{ fontWeight: '600', marginTop: '18px' }}
                    />

                    {/* Match criteria */}
                    <PasswordChecklist criteria={criteria} isConfirmPassword={true} showErrors={showErrors} />

                    {/* Server error (e.g. the code expired between steps) */}
                    {error && (
                        <div className={styles.error}>
                            <img
                                src={exclamation}
                                alt="Error"
                                className={styles.errorIcon}
                            />
                            <div className={styles.errorMessage}>
                                {error}
                            </div>
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        className={`${shared.authButton} ${isLoading ? shared.buttonLoading : ''}`}
                        style={{ marginTop: '24px' }}
                        onClick={handleReset}
                    >
                        <div className={shared.buttonContent}>
                            {isLoading ? "Resetting..." : "Reset password"}
                            {isLoading && <span className={shared.spinner}></span>}
                        </div>
                    </button>
                </div>
            </div>
        </>
    );
};

export default ResetPasswordPage;
